import React, {useEffect, useMemo, useState} from "react";
import ScheduleApi from "../../../../common/api/functions/Schedule";
import SetupApi from "../../../../common/api/functions/Setup";
import {PaginationInterface, structPagination} from "../../../../interfaces/PaginationInterface";
import {ResponseInterface} from "../../../../interfaces/ResponseInterface";
import {config} from "../../../../config";
import QLoading from "../../../../common/components/QLoading";
import QHeaderTable from "../../../../common/components/Genneral/QHeaderTable";
import QPagination from "../../../../common/components/QPaging";
import {useTranslation} from "react-i18next";
import {ScheduleConfig} from "./config";
import QButton from "../../../../common/components/QButton";
import QModal from "../../../../common/components/QModal";
import BodyScheduleSetupComponent from "./components/BodyScheduleSetupComponent";
import {ScheduleInterface} from "../../../../interfaces/private/ScheduleInterface";
import QButtonDelete from "../../../../common/components/QButton/QButtonDelete";
import QButtonSwitch from "../../../../common/components/QButton/QButtonSwitch";
import SettingsPageHeader from "../components/SettingsPageHeader";
import SettingsPanelCard from "../components/SettingsPanelCard";
import {getSyncEngineVersion} from "../../../../common/helpers/syncEngine";
import {getEffectiveSyncModules} from "../../../../common/helpers/setupCompleteness";
import {SyncModuleKey} from "../../../public/SetupV2/helpers/onboardingModules";
import {SetupInterface} from "../../../../interfaces/private/SetupInterface";
import {SYNC_NAV_ITEMS} from "../../../../common/helpers/syncNavModules";

const Schedule: React.FC = () => {
    const {t} = useTranslation();
    const isV2 = getSyncEngineVersion() === "v2";

    const FrequencyOptions = useMemo(() => [
        {value: "0 * * * *", label: t("schedule.freqHourly")},
        {value: "0 0 * * *", label: t("schedule.freqDailyMidnight")},
        {value: "0 6,18 * * *", label: t("schedule.freqDailyTwice")},
    ], [t]);

    const StatusOptions = useMemo(() => [
        {value: "active", label: t("schedule.statusActive")},
        {value: "inactive", label: t("schedule.statusInactive")},
    ], [t]);

    const [loading, setLoading] = useState(true);
    const [setup, setSetup] = useState<SetupInterface>({});
    const [pagination, setPagination] = useState<PaginationInterface | any>({
        ...structPagination,
    });
    const [createNewClick, setCreateNewClick] = useState(false);
    const [schedules, setSchedules] = useState<ScheduleInterface[]>([]);

    const typeLabel = (type: string) => {
        const item = SYNC_NAV_ITEMS.find((i) => i.key === type);
        return item ? t(item.labelKey) : type.charAt(0).toUpperCase() + type.slice(1);
    };

    const allObjects = useMemo(() => {
        if (!isV2) {
            return ["payment", "payout", "subscription", "invoice"];
        }
        return getEffectiveSyncModules(setup);
    }, [isV2, setup]);

    const missingModules = useMemo(() => {
        if (!isV2) {
            return [] as SyncModuleKey[];
        }
        const scheduled = new Set(schedules.map((s) => s.type));
        return allObjects.filter((key) => !scheduled.has(key));
    }, [isV2, allObjects, schedules]);

    useEffect(() => {
        if (loading) {
            loadSchedule();
        }
    }, [loading]);

    const loadSchedule = async () => {
        setLoading(true);
        try {
            const requests: Promise<unknown>[] = [
                ScheduleApi.getAll(pagination).then(async (response: ResponseInterface | any) => {
                    if (response.status === config.STATUS_CODE.OK) {
                        setSchedules(response.data.data.data || []);
                        setPagination({
                            ...pagination,
                            page: response.data.data.current_page,
                            total: response.data.data.total,
                            per_page: response.data.data.per_page
                        });
                    }
                }),
            ];
            if (isV2) {
                requests.push(
                    SetupApi.detail().then(async (response: ResponseInterface | any) => {
                        if (response?.status === config.STATUS_CODE.OK) {
                            setSetup(response?.data?.data || {});
                        }
                    })
                );
            }
            await Promise.all(requests);
        } catch {
            // ignore
        } finally {
            await setLoading(false);
        }
    };

    const deleteSchedule = async (id: number) => {
        if (!window.confirm(t("schedule.deleteConfirm"))) {
            return;
        }
        setLoading(true);
        try {
            await ScheduleApi.delete(id).then(async (response: ResponseInterface | any) => {
                if (response.status === config.STATUS_CODE.OK) {
                    await loadSchedule();
                }
            });
        } catch {
            // ignore
        } finally {
            await setLoading(false);
        }
    };

    return (
        <React.Fragment>
            <QModal
                open={createNewClick}
                setOpen={setCreateNewClick}
                headerContent={t("schedule.modalTitle")}
                bodyContent={(
                    <BodyScheduleSetupComponent
                        setOpen={setCreateNewClick}
                        allObjects={allObjects}
                        schedules={schedules}
                        FrequencyOptions={FrequencyOptions}
                        StatusOptions={StatusOptions}
                        loadSchedule={loadSchedule}
                        typeLabel={isV2 ? typeLabel : undefined}
                    />
                )}
            />
            <QLoading loading={loading}/>
            <div className="row">
                <SettingsPageHeader
                    title={t("schedule.pageTitle")}
                    subtitle={t("schedule.pageSubtitle")}
                    showBackToDashboard={isV2}
                    actions={(
                        <QButton
                            type="primary"
                            onClick={() => setCreateNewClick(true)}
                            content={t("schedule.new")}
                            disabled={isV2 && allObjects.length === 0}
                        />
                    )}
                />
                <div className="col-lg-12">
                    {isV2 && missingModules.length > 0 && !loading ? (
                        <div className="alert alert-warning mb-3" role="status">
                            <strong>{t("schedule.missingTitle")}</strong>
                            <div className="small mt-1">
                                {t("schedule.missingHelp", {
                                    modules: missingModules.map((m) => typeLabel(m)).join(", "),
                                })}
                            </div>
                        </div>
                    ) : null}
                    <SettingsPanelCard
                        title={t("schedule.listTitle")}
                        help={t("schedule.listHelp")}
                        badge={schedules.length > 0 ? {
                            label: t("schedule.countBadge", {count: schedules.length}),
                            variant: "success",
                        } : undefined}
                    >
                        {schedules.length === 0 && !loading ? (
                            <div className="text-center py-4">
                                <p className="mb-1 fw-semibold">{t("schedule.empty")}</p>
                                <p className="text-muted small mb-3">{t("schedule.emptyHelp")}</p>
                                <QButton
                                    type="primary"
                                    onClick={() => setCreateNewClick(true)}
                                    content={t("schedule.new")}
                                    disabled={isV2 && allObjects.length === 0}
                                />
                            </div>
                        ) : (
                            <div className="mb-3">
                                <table className="table align-middle table-nowrap mb-0" id="customerTable">
                                    <QHeaderTable
                                        paging={pagination}
                                        setPaging={async (p: any) => {
                                            await setPagination(p);
                                            await setLoading(true);
                                        }}
                                        headers={ScheduleConfig.headers(t)}
                                        funcChecked={() => {}}
                                    />
                                    <tbody className="list form-check-all">
                                    {schedules?.map((schedule: ScheduleInterface) => (
                                        <tr key={schedule.id}>
                                            <td>
                                                {isV2
                                                    ? typeLabel(schedule.type)
                                                    : schedule.type.charAt(0).toUpperCase() + schedule.type.slice(1)}
                                            </td>
                                            <td>{FrequencyOptions.find(a => a.value === schedule.frequency)?.label}</td>
                                            <td>
                                                <QButtonSwitch
                                                    item={schedule}
                                                    getStatus={(item) => item.status === "active"}
                                                    onToggle={async (_id, newStatus) => {
                                                        await ScheduleApi.update(schedule.id, {
                                                            status: newStatus ? 'active' : 'inactive'
                                                        });
                                                    }}
                                                />
                                            </td>
                                            <td>
                                                <QButtonDelete onDelete={() => deleteSchedule(schedule.id)}/>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        {schedules.length > 0 ? (
                            <QPagination
                                pagination={pagination}
                                setPagination={setPagination}
                                setLoading={setLoading}
                            />
                        ) : null}
                    </SettingsPanelCard>
                </div>
            </div>
        </React.Fragment>
    );
};

export default Schedule;
